import React from "react"
import { Pressable, Linking } from "react-native"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { H5, Small } from "@/components/standard/BFText"
import { DynamicColorCircle } from "@/components/standard/DynamicColorCircle"
import { BFColors } from "@/constants/Colors"
import PhoneIcon from "phosphor-react-native/src/icons/Phone"
import EmailIcon from "phosphor-react-native/src/icons/Envelope"
import { showSuccessToast } from "@/components/toasts/AppToast"
import { formatPhoneNumberString } from "@/utils/StringUtils"

interface LoanTeamCardProps {
  name: string
  role: string
  phoneNumber?: string
  emailAddress?: string
  circleIcon: React.ReactNode
  circleColor?: string
}

const LoanTeamCard = ({
  name,
  role,
  phoneNumber,
  emailAddress,
  circleIcon,
  circleColor = BFColors.beige,
}: LoanTeamCardProps) => {
  // Attempt to open the phone dialer, otherwise show a toast with the phone number.
  const handlePhonePress = async () => {
    if (!phoneNumber) return
    const url = `tel:${phoneNumber}`
    try {
      const supported = await Linking.canOpenURL(url)
      if (supported) {
        await Linking.openURL(url)
      } else {
        showSuccessToast({
          title: "Unable to Dial from this Device",
          message: `Please call ${formatPhoneNumberString(phoneNumber)}`,
          visibilityTime: 100000,
        })
      }
    } catch (error) {
      console.error("Error opening phone dialer", error)
      showSuccessToast({
        title: "Unable to Dial from this Device",
        message: `Please call ${formatPhoneNumberString(phoneNumber)}`,
        visibilityTime: 100000,
      })
    }
  }

  // Attempt to open the email client, otherwise show a toast with the email address.
  const handleEmailPress = async () => {
    if (!emailAddress) return
    const url = `mailto:${emailAddress}`
    try {
      const supported = await Linking.canOpenURL(url)
      if (supported) {
        await Linking.openURL(url)
      } else {
        showSuccessToast({
          title: "Unable to Email from this Device",
          message: `Please email ${emailAddress}`,
          visibilityTime: 100000,
        })
      }
    } catch (error) {
      console.error("Error opening email client", error)
      showSuccessToast({
        title: "Unable to Email from this Device",
        message: `Please email ${emailAddress}`,
        visibilityTime: 100000,
      })
    }
  }

  return (
    <Vertical className="h-[80px] web:h-[72px] bg-white border rounded-md border-light-grey mb-2 justify-center">
      <Horizontal className="items-center justify-between px-6">
        {/* Left side: circular icon and member details */}
        <Horizontal className="ml-1">
          <DynamicColorCircle color={circleColor} size={36}>
            {circleIcon}
          </DynamicColorCircle>
          <Vertical className="gap-0.5 ml-1">
            <H5>{role}</H5>
            <Small>{name}</Small>
          </Vertical>
        </Horizontal>
        {/* Right side: pressable phone and email icons */}
        <Horizontal className="items-center gap-3">
          {phoneNumber && (
            <Pressable
              testID={"phone-call-button"}
              onPress={() => {
                void handlePhonePress()
              }}
            >
              <PhoneIcon weight="light" />
            </Pressable>
          )}
          {emailAddress && (
            <Pressable
              testID={"send-email-button"}
              onPress={() => {
                void handleEmailPress()
              }}
            >
              <EmailIcon weight="light" />
            </Pressable>
          )}
        </Horizontal>
      </Horizontal>
    </Vertical>
  )
}

export default LoanTeamCard
